<script setup lang="ts">
import BackButton from "@/components/BackButton.vue";
import Page from "@/components/Page.vue";
import DirectoryCard from "@/components/documents//list/DirectoryCard.vue";
import CreateDirectory from "@/components/documents/list/CreateDirectory.vue";
import DocumentCard from "@/components/documents/list/DocumentCard.vue";
import DocumentPreview from "@/components/documents/list/DocumentPreview.vue";

import axios from "axios";
import _debounce from "lodash/debounce";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useStore } from "vuex";

import { useIsMobile } from "@/utils/checkMobile";
import setUserData from "@/utils/setUserData";
import { documentStatuses } from "@/utils/statuses";
import useGTM from "@/utils/gtm";

const { t } = useI18n();
const isMobile = useIsMobile();
const store = useStore();
const isTemporaryUser = computed(() => store.state.auth.user.isTemporary);
const isSubscribed = computed(() => store.state.auth.user.hasValidSubscription);
const directory = computed(() => selectedDirectory.value?.name);
const pageTitle = computed(() =>
  directory.value
    ? `${t("documents.my_documents")} / ${directory.value}`
    : t("documents.my_documents"),
);
const emit = defineEmits(["setCurrentDirectory", "removeDirectory", "createDirectory"]);
const toast = useToast();
const $t = useI18n().t;
const router = useRouter();

const documents = ref([]);
const directories = ref([]);
const dialog = ref(false);
const currentPage = ref(1);
const isLastPage = ref(false);
const isLoading = ref(false);
const selectedDocumentId = ref(null);
const documentToMove = ref(null);

const shouldRedirectToLatestDocument = computed(
  () => router.currentRoute.value.name === "payment-success",
);

const handlePurchaseEvent = async () => {
  await useGTM.handlePaymentSuccess(router, axios);
};

const redirectToLatestDocument = () => {
  const currentRoute = router.currentRoute.value;
  const templateId = currentRoute.query.template;
  const documentId = currentRoute.query.documentId;

  // If we have both template and document ID from query params (from our payment success URL)
  // redirect to document-questions flow instead of new-document-creator
  if (templateId && documentId) {
    router.push({
      name: "document-questions",
      query: {
        template: templateId,
        documentId: documentId,
      },
    });
    return;
  }

  // Since backend ignores our success_url, check if latest document has a template_id
  // and try to redirect to document-questions instead of new-document-creator
  const latestDocument = documents.value[0];

  if (latestDocument && latestDocument.template_id) {
    console.log('🔄 Payment success: redirecting to document-questions flow');
    router.push({
      name: "document-questions",
      query: {
        template: latestDocument.template_id,
        documentId: latestDocument.id,
      },
    });
    return;
  }

  // Fallback to default behavior
  router.push({
    name: "new-document-creator",
    params: {
      uuid: latestDocument.id,
    },
  });
};

const showDialog = (documentId = null) => {
  dialog.value = true;
  documentToMove.value = documentId;
};

const documentStatusList = documentStatuses.map((status) => ({
  title: $t("documents.statuses." + status.name),
  value: status.value,
}));

const documentNameFilter = ref(null);
const onDocumentNameFilter = _debounce(() => getDocuments(true), 500);

const documentStatusFilter = ref(null);
const directoryFilter = ref("null");

const removeDirectory = async () => {
  try {
    await axios.delete(`/api/directories/${directoryFilter.value}`);
    toast.success($t("notifications.directory_removed"));
    setCurrentDirectory("null");
    await getDirectories();
  } catch (error) {
    toast.error($t("errors.documents.directory_removal_error"));
  }
};

const getDocuments = async (force = false) => {
  isLoading.value = true;

  if (force === true) {
    currentPage.value = 1;
  }

  try {
    const { data } = await axios.get("/api/documents", {
      params: {
        page: currentPage.value,
        per_page: 24,
        "filter[name]": documentNameFilter.value,
        "filter[status]": documentStatusFilter.value,
        "filter[directory_id]": directoryFilter.value,
        sort: "-created_at",
      },
    });

    if (!documents.value.length || force === true) {
      documents.value = data.data;
    } else {
      documents.value.push(...data.data);
    }
    currentPage.value++;
    isLastPage.value = !data.next_page_url;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const updateDirectoriesWithFilteredCounts = async () => {
  // Get filtered document count for each directory
  for (const directory of directories.value) {
    try {
      const { data } = await axios.get("/api/documents", {
        params: {
          page: 1,
          per_page: 1, // We only need the count, not the actual documents
          "filter[name]": documentNameFilter.value,
          "filter[status]": documentStatusFilter.value,
          "filter[directory_id]": directory.id,
        },
      });
      // Store the filtered count in a new property
      directory.filtered_documents_count = data.total || 0;
    } catch (error) {
      console.error(`Error getting filtered count for directory ${directory.id}:`, error);
      directory.filtered_documents_count = 0;
    }
  }
};

const getDirectories = async () => {
  if (isTemporaryUser.value || !isSubscribed.value) {
    return;
  }

  try {
    const { data } = await axios.get("/api/directories");
    directories.value = data.data;

    // If we have active filters, get filtered counts for each directory
    if (documentNameFilter.value || documentStatusFilter.value) {
      await updateDirectoriesWithFilteredCounts();
    }
  } catch (error) {
    console.error(error);
  }
};

const moveToDirectory = async (directoryId, documentId) => {
  try {
    await axios.put(`/api/documents/${documentId}/directory`, {
      directory_id: directoryId,
    });
    useToast().success($t("notifications.document_moved"));
    refreshPage();
  } catch (error) {
    useToast().error($t("errors.documents.move_error"));
  }
};

const handleOnCreateDirectory = async (directoryId) => {
  await getDirectories();
  if (documentToMove.value) {
    await moveToDirectory(directoryId, documentToMove.value);

    documentToMove.value = null;
  }
};

const refreshPage = async () => {
  await Promise.all([getDocuments(true), getDirectories()]);
};

const setCurrentDirectory = (id) => {
  isLoading.value = true;
  documents.value = [];
  directoryFilter.value = id ?? "null";
};

const selectedDirectory = computed(() =>
  directories.value.find((directory) => directory.id === directoryFilter.value),
);

// Filter directories based on whether they have matching documents when filters are applied
const visibleDirectories = computed(() => {
  if (!documentNameFilter.value && !documentStatusFilter.value) {
    // No filters applied, show all directories
    return directories.value;
  }

  // Filters are applied, only show directories that have matching documents
  return directories.value.filter(directory => {
    const filteredCount = directory.filtered_documents_count ?? directory.documents_count;
    return filteredCount > 0;
  });
});

const debouncedGetDocuments = _debounce(() => getDocuments(true), 300);

const handleOpenDocument = (clickedDocument) => (selectedDocumentId.value = clickedDocument);

const clearFilters = () => {
  documentNameFilter.value = null;
  documentStatusFilter.value = null;
};

const getResultsText = () => {
  const count = documents.value.length;
  if (count === 0) {
    return t('documents.no_results');
  }
  return t('documents.showing_results', { count });
};

watch([documentStatusFilter, directoryFilter], () => debouncedGetDocuments());

// Update directory filtered counts when name filter changes
watch([documentNameFilter], async () => {
  if (documentNameFilter.value !== null && directories.value.length > 0) {
    await updateDirectoriesWithFilteredCounts();
  } else if (documentNameFilter.value === null) {
    // Clear filtered counts when name filter is cleared
    directories.value.forEach(directory => {
      delete directory.filtered_documents_count;
    });
  }
});

// Update directory filtered counts when status filter changes
watch([documentStatusFilter], async () => {
  if (directories.value.length > 0) {
    if (documentStatusFilter.value !== null || documentNameFilter.value !== null) {
      await updateDirectoriesWithFilteredCounts();
    } else {
      // Clear filtered counts when all filters are cleared
      directories.value.forEach(directory => {
        delete directory.filtered_documents_count;
      });
    }
  }
});

onMounted(async () => {
  await refreshPage();

  // Handle purchase event if coming from payment processor
  if (shouldRedirectToLatestDocument.value) {
    await handlePurchaseEvent();
    await setUserData();
    redirectToLatestDocument();
  }

  //open document from url
  const uuidInRoute = router.currentRoute.value.params.documentId;
  if (uuidInRoute) {
    selectedDocumentId.value = uuidInRoute;
  }
});
</script>

<template>
  <div class="documents-page">
    <!-- Modern Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="back-button-container">
            <v-btn
              @click.stop="setCurrentDirectory('null')"
              variant="text"
              size="large"
              class="modern-back-button"
              v-if="directory"
            >
              <v-icon size="20">mdi-arrow-left</v-icon>
            </v-btn>
            <v-btn
              :to="'/new-document'"
              variant="text"
              size="large"
              class="modern-back-button"
              v-else
            >
              <v-icon size="20">mdi-arrow-left</v-icon>
            </v-btn>
          </div>
          <div class="page-title-section">
            <h1 class="page-title">{{ pageTitle }}</h1>
            <p class="page-subtitle" v-if="!directory">
              {{ $t('documents.manage_documents_subtitle') }}
            </p>
            <div v-if="directory" class="breadcrumb">
              <v-chip
                size="small"
                variant="outlined"
                prepend-icon="mdi-folder"
                class="directory-chip"
              >
                {{ directory }}
              </v-chip>
            </div>
          </div>
        </div>

        <div class="header-actions">
          <v-btn
            @click="showDialog(null)"
            variant="outlined"
            class="action-button mr-3"
            v-if="!directory && !isTemporaryUser && isSubscribed"
          >
            <template v-slot:prepend>
              <v-icon size="18">mdi-folder-plus</v-icon>
            </template>
            {{ $t("documents.create_directory") }}
          </v-btn>

          <v-btn
            variant="outlined"
            class="action-button mr-3"
            v-if="!!directory"
            @click="removeDirectory"
          >
            <template v-slot:prepend>
              <v-icon size="18">mdi-folder-remove</v-icon>
            </template>
            {{ $t("documents.remove_directory") }}
          </v-btn>

          <RouterLink to="/new-document">
            <v-btn class="primary-button">
              <template v-slot:prepend>
                <v-icon size="18">mdi-plus</v-icon>
              </template>
              {{ $t("documents.create_document") }}
            </v-btn>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="page-content" v-if="!shouldRedirectToLatestDocument">
      <DocumentPreview
        v-if="selectedDocumentId"
        :documentId="selectedDocumentId"
        @close="selectedDocumentId = null"
      />

      <!-- Modern Filters -->
      <div class="filters-section">
        <div class="filters-container">
          <div class="search-input-container">
            <v-text-field
              v-model="documentNameFilter"
              @input="onDocumentNameFilter"
              variant="outlined"
              density="comfortable"
              :placeholder="$t('documents.search_documents')"
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              hide-details
              clearable
            />
          </div>

          <div class="filter-controls">
            <v-select
              v-model="documentStatusFilter"
              :items="documentStatusList"
              variant="outlined"
              density="comfortable"
              :label="$t('documents.filter_by_status')"
              class="status-filter"
              clearable
              hide-details
            />

            <v-btn
              variant="text"
              class="filter-reset"
              v-if="documentNameFilter || documentStatusFilter"
              @click="clearFilters"
            >
              <v-icon size="18">mdi-filter-remove</v-icon>
              {{ $t('common.clear_filters') }}
            </v-btn>
          </div>
        </div>

        <!-- Results count -->
        <div class="results-info" v-if="!isLoading">
          <span class="results-count">{{ getResultsText() }}</span>
        </div>
      </div>

      <!-- Directories Section -->
      <div v-if="directoryFilter === 'null' && visibleDirectories.length > 0" class="directories-section">
        <h2 class="section-title">
          <v-icon class="section-icon">mdi-folder-multiple</v-icon>
          {{ $t('documents.directories') }}
        </h2>
        <div class="directories-grid">
          <DirectoryCard
            v-for="directory in visibleDirectories"
            :key="directory.id"
            :directory="directory"
            @setCurrentDirectory="setCurrentDirectory"
            @removeDirectory="removeDirectory"
          />
        </div>
      </div>

      <!-- Documents Section -->
      <div class="documents-section">
        <h2 class="section-title" v-if="directoryFilter === 'null' && visibleDirectories.length > 0">
          <v-icon class="section-icon">mdi-file-document-multiple</v-icon>
          {{ $t('documents.recent_documents') }}
        </h2>

        <!-- Loading State -->
        <div v-if="isLoading && documents.length === 0" class="loading-state">
          <div class="loading-grid">
            <div v-for="i in 8" :key="i" class="document-skeleton">
              <v-skeleton-loader type="card" />
            </div>
          </div>
        </div>

        <!-- Documents Grid -->
        <template v-else-if="documents.length">
          <div class="documents-grid">
            <DocumentCard
              v-for="document in documents"
              :key="document.id"
              :document-statuses="documentStatuses"
              :document="document"
              :directories="directories"
              :show-delete-button="true"
              @refresh-page="refreshPage"
              @open-document="handleOpenDocument"
              @createDirectory="showDialog"
            />
          </div>

          <!-- Load More Button -->
          <div class="load-more-section" v-if="!isLastPage">
            <v-btn
              variant="outlined"
              size="large"
              :loading="isLoading"
              @click="getDocuments"
              class="load-more-button"
            >
              {{ $t("common.load_more") }}
            </v-btn>
          </div>
        </template>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-illustration">
            <v-icon size="80" color="#CBD5E1">mdi-file-document-outline</v-icon>
          </div>
          <h3 class="empty-title">{{ $t('documents.no_documents_title') }}</h3>
          <p class="empty-description">{{ $t('documents.no_documents_description') }}</p>
          <RouterLink to="/new-document">
            <v-btn class="primary-button" size="large">
              <template v-slot:prepend>
                <v-icon>mdi-plus</v-icon>
              </template>
              {{ $t('documents.create_first_document') }}
            </v-btn>
          </RouterLink>
        </div>
      </div>
    </div>

    <CreateDirectory v-model="dialog" @onCreate="handleOnCreateDirectory" />
  </div>
</template>

<style scoped lang="scss">
.documents-page {
  min-height: 100vh;
  background: #FAFAFA;
}

.page-header {
  background: white;
  border-bottom: 1px solid #E2E8F0;
  padding: 32px 40px;

  @media (max-width: 768px) {
    padding: 20px 24px;
  }
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.back-button-container {
  flex-shrink: 0;
}

.modern-back-button {
  font-family: 'Archivo', sans-serif;
  color: #64748B !important;
  border-radius: 12px;
  width: 48px;
  height: 48px;
  min-width: 48px;

  &:hover {
    background: rgba(14, 37, 177, 0.08) !important;
    color: #0E25B1 !important;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
}

.page-title-section {
  flex: 1;
}

.page-title {
  font-family: 'Archivo', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 24px;
  }
}

.page-subtitle {
  font-size: 16px;
  color: #64748B;
  margin: 4px 0 0 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.breadcrumb {
  margin-top: 8px;
}

.directory-chip {
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
}

.action-button {
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  text-transform: none;
  border-radius: 100px !important;
  height: 40px;

  ::v-deep .v-btn__content {
    border-radius: 100px !important;
  }

  ::v-deep .v-btn__overlay {
    border-radius: 100px !important;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.primary-button {
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  text-transform: none;
  background: #0E25B1 !important;
  color: white !important;
  border-radius: 100px !important;
  height: 40px;

  ::v-deep .v-btn__content {
    border-radius: 100px !important;
  }

  ::v-deep .v-btn__overlay {
    border-radius: 100px !important;
  }

  &:hover {
    background: #0A1D8F !important;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.page-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
}

.filters-section {
  margin-bottom: 32px;
}

.filters-container {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.search-input-container {
  flex: 1;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: none;
  }
}

.search-input {
  font-family: 'Archivo', sans-serif;

  ::v-deep .v-field {
    background: white;
    border-radius: 12px;
  }
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
}

.status-filter {
  min-width: 200px;
  font-family: 'Archivo', sans-serif;

  ::v-deep .v-field {
    background: white;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    min-width: auto;
  }
}

.filter-reset {
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  text-transform: none;
}

.results-info {
  padding: 0 4px;
}

.results-count {
  font-size: 14px;
  color: #64748B;
  font-weight: 500;
}

.section-title {
  font-family: 'Archivo', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #1E293B;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  color: #0E25B1 !important;
}

.directories-section {
  margin-bottom: 48px;
}

.directories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  animation: fadeInUp 0.6s ease-out;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.documents-section {
  margin-bottom: 32px;
}

.loading-state {
  margin: 32px 0;
}

.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.document-skeleton {
  height: 280px;
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  animation: fadeInUp 0.6s ease-out;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.load-more-section {
  display: flex;
  justify-content: center;
  margin-top: 48px;
}

.load-more-button {
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  text-transform: none;
  border-radius: 100px;
  min-width: 160px;
}

.empty-state {
  text-align: center;
  padding: 80px 32px;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
}

.empty-illustration {
  margin-bottom: 24px;
}

.empty-title {
  font-family: 'Archivo', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #1E293B;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
}

.empty-description {
  font-size: 16px;
  color: #64748B;
  margin: 0 0 32px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 14px;
  }
}
</style>

<style lang="scss">
// Global styles (non-scoped) to override Vuetify button styles
.documents-page .action-button.v-btn {
  border-radius: 100px !important;
}

.documents-page .action-button.v-btn .v-btn__content {
  border-radius: 100px !important;
}

.documents-page .action-button.v-btn .v-btn__overlay {
  border-radius: 100px !important;
}

.documents-page .primary-button.v-btn {
  border-radius: 100px !important;
}

.documents-page .primary-button.v-btn .v-btn__content {
  border-radius: 100px !important;
}

.documents-page .primary-button.v-btn .v-btn__overlay {
  border-radius: 100px !important;
}

.documents-page .load-more-button.v-btn {
  border-radius: 100px !important;
}

.documents-page .load-more-button.v-btn .v-btn__content {
  border-radius: 100px !important;
}

.documents-page .load-more-button.v-btn .v-btn__overlay {
  border-radius: 100px !important;
}
</style>
